import { LeadSourceType, ServicerLeadSource } from '@mortgage-pos/types'

// For new sources, add a new object to the map where the the key is the "LeadSource" string
// being passed alongside a "LeadSourceId"
// - name: this is the string that will be stored in the "source" column on the Applications table
// - trackingField (optional): this is the name of the property to store the Id value as in Application_Tracking
// - sourceField (optional): legacy BankRate request handler field name to find in request data
type LeadSourceMap = {
  [s in LeadSourceType]: LeadSource
}

export type LeadSource = {
  name: string
  displayName?: string // How the lead source will be displayed in the E2 overview page
  trackingField?: string
  sourceField?: string
  urlMatchString?: string
  urlSecondaryMatchString?: string
  urlMatchParams?: string[]
  isBuilder?: boolean
  servicer?: ServicerLeadSource
  manualRoutingOnly?: boolean // never auto-routed to an LO; Ops assigns by hand
}

export const leadSourceMap: Partial<LeadSourceMap> = {
  ALFALFA: {
    name: 'Alfalfa',
  },
  ALTAIREQUIFAX: {
    name: 'AltairEquifax',
    displayName: 'EquifaxLoanCare',
  },
  ALTAIREXPERIAN: {
    name: 'AltairExperian',
    displayName: 'ExperianLoanCare',
  },
  ALTAIREXPERIANREALTIME: {
    name: 'AltairExperianRealtime',
    displayName: 'ExperianRealtimeLoanCare',
  },
  ALTAIRTRANSUNION: {
    name: 'AltairTransunion',
    displayName: 'TransunionLoanCare',
  },
  ALTAIRPREMOVER: {
    name: 'AltairPreMover',
    displayName: 'PreMoverLoanCare',
  },
  AGC: {
    name: 'AGC',
    urlMatchString: 'src=agc',
    isBuilder: true,
  },
  AGCINCENTIVE: {
    name: 'AGCIncentive',
    urlMatchString: 'src=agcincentive',
    isBuilder: true,
  },
  AKCHOMES: {
    name: 'AKCHomes',
    urlMatchString: 'src=akchomes',
    isBuilder: true,
  },
  AKCHOMESINCENTIVE: {
    name: 'AKCHomesIncentive',
    urlMatchString: 'src=akchomesincentive',
    isBuilder: true,
  },
  AMAZON: {
    name: 'Amazon',
    urlMatchString: 'src=2f49875e',
  },
  AMAZONBENEFITSDEMO: {
    name: 'AmazonBenefitsDemo',
  },
  ANNALYLOANCARE: {
    name: 'AnnalyLoanCare',
    displayName: 'LoanCare',
    urlMatchString: 'src=loancare',
    servicer: ServicerLeadSource.Annaly,
  },
  ANNALYLOANCAREMONEYOUTBOUND: {
    name: 'annalyLoanCareMoneyOutbound',
    displayName: 'LoanCareMoneyOutbound',
  },
  ANNALYLOANCAREPAYOFF: {
    name: 'annalyLoanCarePayoff',
    displayName: 'LoanCarePayoff',
  },
  ANNALYLOANCAREWARMTRANSFER: {
    name: 'annalyLoanCareWarmTransfer',
    displayName: 'LoanCare Warm Transfer',
  },
  BANKRATE: {
    name: 'Bankrate',
    trackingField: 'bankrateLeadId',
    sourceField: 'bankrateLeadId',
  },
  BANKRATEDIGIFLEX: {
    name: 'BankrateDigiFlex',
    displayName: 'Bankrate',
    trackingField: 'bankrateLeadId',
    sourceField: 'bankrateLeadId',
  },
  BANKRATECPC: {
    name: 'BankrateCPC',
    displayName: 'Bankrate',
    urlMatchString: 'cpc=true',
  },
  BANKRATEEMAIL: {
    name: 'BankrateEmail',
    displayName: 'Bankrate',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=BankrateEmail',
  },
  BANKRATEPRIMARY: {
    name: 'BankratePrimary',
    displayName: 'Bankrate',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
  },
  BANKRATESECONDARY: {
    name: 'BankrateSecondary',
    displayName: 'Bankrate',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
  },
  BANKRATEALFIE: {
    name: 'BankrateAlfie',
    displayName: 'Bankrate',
  },
  BANKRATEFEDEMAIL: {
    name: 'BankrateFedEmail',
    displayName: 'Bankrate',
    urlMatchString: 'utm_campaign=ed_ho_bn_fed_pre',
  },
  BANKRATECASHOUTEMAIL: {
    name: 'BankrateCashOutEmail',
    displayName: 'Bankrate',
    urlMatchString: 'src=br439',
  },
  BANKRATEWSJ: {
    name: 'BankrateWSJ',
    displayName: 'BankrateWSJ',
  },
  BACKFILL: {
    name: 'Backfill',
  },
  BARCELONA: {
    name: 'Barcelona',
    urlMatchString: 'src=barcelona',
    isBuilder: true,
  },
  BARCELONAINCENTIVE: {
    name: 'BarcelonaIncentive',
    urlMatchString: 'src=barcelonaincentive',
    isBuilder: true,
  },
  BESTMONEY: {
    name: 'BestMoney',
    // Best Money mints a prop_id per campaign (11123 HELOC, 11130 purchase,
    // 11131 refinance), so match any prop_id; the duplicate 'prop_id=' pads to
    // 3 params to sort ahead of the generic RVMN fallback, and the match string
    // catches urls with none.
    urlMatchParams: ['vendor=BestMoney', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=BestMoney',
  },
  BESTMONEYCASHOUTCLICK: {
    name: 'BestMoneyCashoutClick',
    // Padded to 4 params so this campaign keeps precedence over the generic
    // BestMoney vendor match above.
    urlMatchParams: ['click_id=', 'prop_id=47109', 'prop_id=', 'prop_id='],
  },
  BUILDERREFERRAL: {
    name: 'BuilderReferral',
    isBuilder: true,
  },
  BUILDER: {
    name: 'Builder',
    urlMatchString: 'src=builder',
    isBuilder: true,
  },
  BUILDERINCENTIVE: {
    name: 'BuilderIncentive',
    urlMatchString: 'src=builderincentive',
    isBuilder: true,
  },
  BING: {
    name: 'Bing',
    urlMatchString: 'utm_source=bing',
  },
  BORROWERPORTAL: {
    name: 'Borrower Portal',
  },
  CONFIA: {
    name: 'Confia',
    urlMatchString: 'src=confia',
    isBuilder: true,
  },
  CONFIAINCENTIVE: {
    name: 'ConfiaIncentive',
    urlMatchString: 'src=confiaincentive',
    isBuilder: true,
  },
  CONSUMERAFFAIRS: {
    name: 'ConsumerAffairs',
    urlMatchString: 'source=consumeraffairs',
  },
  CPC: {
    name: 'CPC',
    urlMatchString: 'click_id=',
  },
  CUSTOMERREFERRAL: {
    name: 'CustomerReferral',
  },
  DIRECTRESIDENTIAL: {
    name: 'DirectResidential',
    urlMatchString: 'src=directresidential',
    isBuilder: true,
  },
  DIRECTRESIDENTIALINCENTIVE: {
    name: 'DirectResidentialIncentive',
    urlMatchString: 'src=directresidentialincentive',
    isBuilder: true,
  },
  ELIJAHANTHONY: {
    name: 'ElijahAnthony',
    urlMatchString: 'src=elijahanthony',
    isBuilder: true,
  },
  ELIJAHANTHONYINCENTIVE: {
    name: 'ElijahAnthonyIncentive',
    urlMatchString: 'src=elijahanthonyincentive',
    isBuilder: true,
  },
  ENCORE: {
    name: 'Encore',
    urlMatchString: 'src=encore',
    isBuilder: true,
  },
  ENCOREINCENTIVE: {
    name: 'EncoreIncentive',
    urlMatchString: 'src=encoreincentive',
    isBuilder: true,
  },
  EMPLOYEELOANS: {
    name: 'EmployeeLoans',
    urlMatchString: 'src=employee',
  },
  EARNNEST: {
    name: 'Earnnest',
  },
  // Aliases: the partner setup form sends the lead source as "Earnnest Referral".
  // getLeadSource() only trims and uppercases (it does not collapse internal
  // whitespace), so the spaced variant resolves to "EARNNEST REFERRAL".
  // Cover the space-free variant too so either payload value maps to canonical.
  'EARNNEST REFERRAL': {
    name: 'Earnnest',
  },
  EARNNESTREFERRAL: {
    name: 'Earnnest',
  },
  EXISTINGCUSTOMERREFERRAL: {
    name: 'ExistingCustomerReferral',
  },
  FACEBOOK: {
    name: 'Facebook',
    trackingField: 'fbclid',
    sourceField: 'fBLeadId',
  },
  FACET: {
    name: 'Facet',
    urlMatchString: 'facet',
  },
  FED: {
    name: 'Fed',
    urlMatchString: 'src=6rlkye84',
  },
  FISPOKE: {
    name: 'FiSpoke',
  },
  FREERATEUPDATE: {
    name: 'FreeRateUpdate',
    trackingField: 'freerateupdateLeadId',
  },
  FULLBEAKER: {
    name: 'FullBeaker',
    // Full Beaker launches with a HELOC campaign (prop_id 11140); match any
    // prop_id so future campaigns map here instead of leaking to RVMN.
    urlMatchParams: ['vendor=fullbeaker', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=fullbeaker',
  },
  HARRISTEETER: {
    name: 'HarrisTeeter',
    urlMatchString: 'src=harristeeter',
  },
  HAVEN: {
    name: 'Haven',
    urlMatchString: 'src=haven',
  },
  HAVENLOANCARE: {
    name: 'havenLoanCare',
    displayName: 'LoanCare',
    urlMatchString: 'utm_source=haven',
  },
  HELOCOUTBOUND: {
    name: 'helocOutbound',
  },
  HOMEEQUITYCPC: {
    name: 'HomeEquityCPC',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=bankratehomeequity',
  },
  HOMEEQUITYPRIMARY: {
    name: 'HomeEquityPrimary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'pid=br_he_rt_co',
  },
  HOMEEQUITYSECONDARY: {
    name: 'HomeEquitySecondary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=bruno:home-equity:quote',
  },
  HOMELIGHT: {
    name: 'HomeLight',
  },
  HSH: {
    name: 'HSH',
    urlMatchString: 'source=HSH',
  },
  ICANBUY: {
    name: 'ICanBuy',
    urlMatchString: 'vendor=mrc',
  },
  KBBHOMES: {
    name: 'KBBHomes',
    displayName: 'KBB Homes',
    urlMatchString: 'vendor=KBBHomes',
  },
  HRM: {
    name: 'HRM',
    urlMatchString: 'src=hrm',
    isBuilder: true,
  },
  HRMINCENTIVE: {
    name: 'HRMIncentive',
    urlMatchString: 'src=hrmincentive',
    isBuilder: true,
  },
  INBOUNDCALL: {
    name: 'InboundCall',
  },
  PROPERTYMATE: {
    name: 'PropertyMate',
  },
  PURCHASEREFERRALS: {
    name: 'PurchaseReferrals',
  },
  LEADPOINTPOSTED: {
    name: 'LeadPointPosted',
  },
  LENDGO: {
    name: 'Lendgo',
    // API payload sends leadSource='Lendgo'; landing page carries vendor=Lendgo&prop_id=11120
    urlMatchParams: ['vendor=lendgo', 'prop_id=11120'],
  },
  LENDINGTREE: {
    name: 'LendingTree',
  },
  LOANESTIMATECOMPARISON: {
    name: 'LoanEstimateComparison',
  },
  LOWERMYBILLS: {
    name: 'LowerMyBills',
    urlMatchString: 'source=LowerMyBills',
  },
  LOWERMYBILLSSHORTFORM: {
    name: 'LowerMyBillsShortForm',
  },
  MYAGENTFINDER: {
    name: 'MyAgentFinder',
    urlMatchString: 'source=MyAgentFinder',
  },
  MILITARYVALOAN: {
    name: 'MilitaryVALoan',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=49937'],
  },
  MONEYGROUP: {
    name: 'MoneyGroup',
    // MoneyGroup mints a prop_id per campaign (11122 purchase, 11131 HELOC), so
    // match any prop_id; the duplicate 'prop_id=' pads to 3 params to sort ahead
    // of the generic RVMN fallback, and the match string catches urls with none.
    urlMatchParams: ['vendor=MoneyGroup', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=MoneyGroup',
  },
  MOONSHOT: {
    name: 'Moonshot',
    // Moonshot uses prop_ids 11135 purchase and 11136 HELOC; match any prop_id.
    urlMatchParams: ['vendor=Moonshot', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=Moonshot',
  },
  MORTGAGELENDERCOMPARISONCASHOUTREFICLICK: {
    name: 'MortgageLenderComparisonCashOutRefiClick',
    urlMatchParams: ['click_id=', 'prop_id=50557'],
  },
  NATURALINTELLIGENCE: {
    name: 'NaturalIntelligence',
    // The vendor is shared with other MortgageLendersComparison campaigns, so
    // prop_id 11123 (HELOC) identifies this one; the duplicate pads to 3 params
    // to sort ahead of the generic RVMN fallback.
    urlMatchParams: [
      'vendor=mortgagelenderscomparison',
      'prop_id=11123',
      'prop_id=11123',
    ],
  },
  // Alias: prop_id 11131 is the purchase/refinance campaign for the same
  // partner, so it resolves to the canonical NaturalIntelligence name.
  NATURALINTELLIGENCEPURCHASEREFI: {
    name: 'NaturalIntelligence',
    urlMatchParams: [
      'vendor=mortgagelenderscomparison',
      'prop_id=11131',
      'prop_id=11131',
    ],
  },
  NERDWALLET: {
    name: 'NerdWallet',
    urlMatchString: 'nerdwallet',
  },
  NERDWALLETPURCHASE: {
    name: 'NerdwalletPurchase',
    displayName: 'NerdWallet',
  },
  NERDWALLETREFINANCE: {
    name: 'NerdwalletRefinance',
    displayName: 'NerdWallet',
  },
  NERDWALLETRATETABLEPURCHASE: {
    name: 'NerdWalletRateTablePurchase',
    urlMatchParams: ['utm_source=nerdwallet', 'prop_id=11133'],
  },
  NERDWALLETRATETABLEREFINANCE: {
    name: 'NerdWalletRateTableRefinance',
    urlMatchParams: ['utm_source=nerdwallet', 'prop_id=11134'],
  },
  PERSONALREFERRAL: {
    name: 'PersonalReferral',
  },
  RATEZIP: {
    name: 'RateZip',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=48731'],
  },
  REALTORREFERRAL: {
    name: 'RealtorReferral',
  },
  RETCLIENTFORLIFEOB: {
    name: 'RetClientForLifeOB',
  },
  REFILY: {
    name: 'Refily',
  },
  REPORTING: {
    name: 'Reporting',
  },
  ROBINHOOD: {
    name: 'Robinhood',
    urlMatchString: 'src=robinhood',
  },
  ROBINHOODCONCIERGE: {
    name: 'RobinhoodConcierge',
    displayName: 'Robinhood Concierge',
    urlMatchString: 'src=robinhoodconcierge',
  },
  ROBINHOODSUMMIT: {
    name: 'RobinhoodSummit',
    displayName: 'Robinhood Summit',
    manualRoutingOnly: true,
  },
  RUTHANDRANDALL: {
    name: 'RuthandRandall',
    urlMatchString: 'src=ruthandrandall',
    isBuilder: true,
  },
  RUTHANDRANDALLINCENTIVE: {
    name: 'RuthandRandallIncentive',
    urlMatchString: 'src=ruthandrandallincentive',
    isBuilder: true,
  },
  RVMN: {
    name: 'RVMN',
    urlMatchParams: ['click_id=', 'prop_id='],
  },
  SAGEMORTGAGEEMAIL: {
    name: 'SageMortgageEmail',
  },
  SAGERADIO: {
    name: 'Sage Radio',
  },
  SAGEWEBSITE: {
    name: 'Sage Website',
  },
  SCHEMATHICS: {
    name: 'Schemathics',
    // Schemathics mints a prop_id per campaign (11123 HELOC, 11124 purchase),
    // so match any prop_id; the duplicate 'prop_id=' pads to 3 params to sort
    // ahead of the generic RVMN fallback.
    urlMatchParams: ['vendor=schem', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=schem',
  },
  SEM: {
    name: 'SEM',
    trackingField: 'gclid',
    sourceField: 'gclid',
    urlMatchString: 'utm_source=google',
  },
  SANTORINI_PROPERTY: {
    name: 'SantoriniProperty',
    urlMatchString: 'src=santorini',
    isBuilder: true,
  },
  SANTORINI_PROPERTY_INCENTIVE: {
    name: 'SantoriniPropertyIncentive',
    urlMatchString: 'src=santoriniincentive',
    isBuilder: true,
  },
  SIMS: {
    name: 'Sims',
    urlMatchString: 'src=sims',
    isBuilder: true,
  },
  SMARTASSET: {
    name: 'SmartAsset',
    urlMatchString: 'sa_mortgage',
  },
  SUPPORTPHONENUMBER: {
    name: 'SupportPhoneNumber',
  },
  THEMORTGAGEREPORT: {
    name: 'TheMortgageReport',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=49492'],
  },
  TOP10: {
    name: 'Top10',
    urlMatchString: 'source=top10',
  },
  TRANSUNION: {
    name: 'TransUnion',
    trackingField: 'tuLeadOfferCode',
  },
  UNMAPPED: {
    name: 'Unmapped',
    trackingField: 'unmappedLeadId',
  },
  USNEWS: {
    name: 'USNews',
    // US News mints a prop_id per campaign (11137 purchase, 11138 HELOC,
    // 11139 refinance); match any prop_id.
    urlMatchParams: ['vendor=usnews', 'prop_id=', 'prop_id='],
    urlMatchString: 'vendor=usnews',
  },
  WHITETREE: {
    name: 'WhiteTree',
    urlMatchString: 'src=whitetree',
    isBuilder: true,
  },
  WHITETREEINCENTIVE: {
    name: 'WhiteTreeIncentive',
    urlMatchString: 'src=whitetreeincentive',
    isBuilder: true,
  },
  YEARYHAUCO: {
    name: 'YearyHauco',
    urlMatchString: 'src=yearyhauco',
    isBuilder: true,
  },
  YEARYHAUCOINCENTIVE: {
    name: 'YearyHaucoIncentive',
    urlMatchString: 'src=yearyhaucoincentive',
    isBuilder: true,
  },
  OWNUP: {
    name: 'Ownup',
  },
  OWNUPEXPERIANTRIGGERS: {
    name: 'OwnupExperianTriggers',
    urlMatchParams: ['vendor=Ownup', 'sub_id=exptrigger'],
  },
  ZILLOW: {
    name: 'Zillow',
    trackingField: 'zillowLeadId',
  },
}

export const leadSourceByNameMap: Record<string, LeadSource> =
  Object.fromEntries(
    Object.values(leadSourceMap).map((source) => [source.name, source])
  )

/**
 * Get the display name for a lead source, falling back to the source name if no displayName is set
 * @param sourceName - The name of the lead source (e.g., 'Bankrate', 'AnnalyLoanCare')
 * @returns The display name or the source name itself
 */
export function getLeadSourceDisplayName(sourceName: string): string {
  const leadSource = leadSourceByNameMap[sourceName]
  return leadSource?.displayName || sourceName
}
